import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiArrowLeft, FiEye } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import PublicExamDetailModal from '../components/PublicExamDetailModal';

function PublicExamsSearch() {
  const [cccd, setCccd] = useState('');
  const [patientName, setPatientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!cccd.trim() || !patientName.trim()) {
      toast.warning('Vui lòng nhập số CCCD và họ tên');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.get('/public/exams-search', {
        params: {
          cccd: cccd.trim(),
          patient_name: patientName.trim(),
          page: 1,
          limit: 100,
        },
      });

      const data = response.data?.data || [];
      setResults(data);
      if (data.length === 0) {
        toast.info('Không tìm thấy ca chụp phù hợp');
      }
    } catch (error) {
      setResults([]);
      toast.error('Không thể tra cứu ca chụp');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Tra cứu ca chụp</h1>
          <Link to="/login" className="inline-flex items-center gap-2 text-primary hover:text-primary-dark text-sm font-medium">
            <FiArrowLeft /> Quay lại đăng nhập
          </Link>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <form onSubmit={handleSearch} className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <input
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Số CCCD"
              value={cccd}
              onChange={(e) => setCccd(e.target.value)}
              disabled={loading}
            />
            <input
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Họ tên bệnh nhân"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
            >
              <FiSearch /> {loading ? 'Đang tra cứu...' : 'Tra cứu'}
            </button>
          </form>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {!hasSearched && <p className="text-sm text-slate-500">Nhập thông tin để bắt đầu tra cứu.</p>}
          {hasSearched && results.length === 0 && !loading && (
            <p className="text-sm text-slate-500">Không có dữ liệu phù hợp.</p>
          )}

          {results.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-600">
                    <th className="py-2 pr-3">Bệnh nhân</th>
                    <th className="py-2 pr-3">Mã BN</th>
                    <th className="py-2 pr-3">Modality</th>
                    <th className="py-2 pr-3">Trạng thái</th>
                    <th className="py-2 pr-3">Thời gian</th>
                    <th className="py-2 pr-3">Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2 pr-3">{item.patient_name || '-'}</td>
                      <td className="py-2 pr-3">{item.patient_id || '-'}</td>
                      <td className="py-2 pr-3">{item.modality || '-'}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          item.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          item.status === 'synced' ? 'bg-blue-100 text-blue-700' :
                          item.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {item.status || '-'}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{item.scheduled_time ? new Date(item.scheduled_time).toLocaleString('vi-VN') : '-'}</td>
                      <td className="py-2 pr-3">
                        <button
                          onClick={() => setSelectedExamId(item.id)}
                          className="inline-flex items-center gap-1 text-primary hover:text-primary-dark hover:underline text-sm font-medium"
                        >
                          <FiEye className="w-4 h-4" /> Xem chi tiết
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedExamId && (
        <PublicExamDetailModal
          examId={selectedExamId}
          onClose={() => setSelectedExamId(null)}
        />
      )}
    </div>
  );
}

export default PublicExamsSearch;
